import React, { useEffect, useState } from 'react';

import { ContentSkeleton } from 'src/components/ContentSkeleton';
import { useDispatch } from 'src/store/useDispatch';
import { setAppAlert } from 'src/store/slice/appAlert';
import {
  AdminPlatformUser,
  UserSummary,
  fetchPlatformUser,
  formatUserDisplayName,
} from 'src/api/adminUsers';
import { convertDate } from 'src/components/Table/helpers';

import FieldCard from '../components/FieldCard';
import { FieldGrid, TabPanel } from '../styled';

type DetailsTabProps = {
  userId: string;
  summary: UserSummary;
  onForbidden?: () => void;
};

const DetailsTab: React.FC<DetailsTabProps> = ({ userId, summary, onForbidden }) => {
  const dispatch = useDispatch();
  const [user, setUser] = useState<AdminPlatformUser | null>(summary.user);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchPlatformUser(userId)
      .then((data) => {
        if (!cancelled) setUser(data);
      })
      .catch((e: any) => {
        if (cancelled) return;
        if (e?.response?.status === 403) {
          onForbidden?.();
          return;
        }
        dispatch(
          setAppAlert({
            message: e?.response?.data?.error ?? 'Failed to load user details',
            isSuccess: false,
          }),
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [userId, dispatch, onForbidden]);

  if (loading && !user) {
    return (
      <TabPanel>
        <ContentSkeleton variant="fields" count={8} />
      </TabPanel>
    );
  }

  const displayName = formatUserDisplayName(user);
  const phone =
    user?.phone_number?.code || user?.phone_number?.number
      ? `${user?.phone_number?.code ?? ''} ${user?.phone_number?.number ?? ''}`.trim()
      : '—';
  const created =
    user?.created_at
      ? `${convertDate(user.created_at).date} - ${convertDate(user.created_at).time}`
      : '—';
  const updated =
    user?.updated_at
      ? `${convertDate(user.updated_at).date} - ${convertDate(user.updated_at).time}`
      : '—';

  return (
    <TabPanel>
      <FieldGrid>
        <FieldCard label="Name" value={displayName} copyValue={displayName} />
        <FieldCard label="ID" value={user?.id || '—'} copyValue={user?.id} />
        <FieldCard
          label="Email"
          value={user?.email || '—'}
          copyValue={user?.email}
        />
        <FieldCard label="Created On" value={created} showCopy={false} />
        <FieldCard label="Phone" value={phone} copyValue={phone !== '—' ? phone : undefined} />
        <FieldCard label="Updated On" value={updated} showCopy={false} />
      </FieldGrid>
    </TabPanel>
  );
};

export default DetailsTab;
